import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import IFormComponent from '@models/IFormComponent';
import classNames from 'classnames';
import { InputHTMLAttributes } from 'react';
import { FieldError, RegisterOptions, useFormContext } from 'react-hook-form';

interface TextInputType
  extends InputHTMLAttributes<HTMLInputElement>,
    IFormComponent {
  readonly displayLabel?: boolean;
  readonly error?: FieldError;
  readonly registerOptions?: RegisterOptions;
  readonly id: string;
  readonly label: string;
  readonly suffix?: string;
  readonly containerClasses?: string;
}

const TextInput = ({
  displayLabel = true,
  id,
  label,
  registerOptions,
  suffix,
  containerClasses,
  ...otherProps
}: TextInputType) => {
  const { register, formState } = useFormContext();
  const error = formState.errors[id] || otherProps.error;
  const errorMessage = typeof error?.message === 'string' ? error.message : '';

  return (
    <div
      className={classNames(
        'tw-relative tw-flex tw-flex-col tw-gap-2 tw-text-grey-70',
        containerClasses,
        otherProps.disabled && 'tw-opacity-60'
      )}
    >
      <Text
        as="label"
        variant="label-12"
        htmlFor={id}
        className={displayLabel ? '' : 'tw-sr-only'}
      >
        {label}
        {registerOptions?.required ? ' *' : ''}
      </Text>

      <div
        className={classNames(
          'tw-relative focus-within:tw-outline-2 focus-within:tw-outline-offset-0 focus-within:tw-outline-black',
          error && 'focus-within:tw-outline-red'
        )}
      >
        <input
          id={id}
          type="text"
          inputMode={otherProps.type === 'number' ? 'decimal' : undefined}
          {...register(id, registerOptions)}
          {...otherProps}
          className={classNames(
            'select-text tw-h-xl tw-w-full tw-rounded tw-border tw-border-grey-60 tw-bg-transparent tw-px-xs tw-py-xxs tw-text-grey-100 tw-outline-black placeholder:tw-text-grey-70',
            suffix ? 'tw-pr-10' : '',
            otherProps.className,
            error ? 'tw-border-red tw-pr-lg' : ''
          )}
        />
        {suffix && (
          <span className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-grey-40">
            {suffix}
          </span>
        )}
        {errorMessage && (
          <>
            <Icon
              name="notification"
              className="tw-absolute tw-right-xs tw-top-xs tw-text-red"
            />
            <Text variant="label-12" className="tw-text-red" role="alert">
              {errorMessage}
            </Text>
          </>
        )}
      </div>
    </div>
  );
};

export default TextInput;
